import { Box, useTheme, Button, InputBase, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import ModalCharge from "../../modal/modalCharge";
import CustomToolbar from "../../utils/CustomToolbar";
import ExcelJS from "exceljs";
import { getPermisosView } from "../../services/permisos.services";

const PermisosView = () => {
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5 },
    { field: "id_usuario", headerName: "ID_USUARIO", flex: 0.7 },
    {
      field: "nombre",
      headerName: "NOMBRE",
      flex: 2,
      cellClassName: "name-column--cell",
    },
    {
      field: "permiso_fecha",
      headerName: "Fecha Permiso",
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return "";
        // Format date as DD/MM/YYYY or your preferred format
        return dayjs(params.value).format("DD/MM/YYYY");
      },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const permisos = await getPermisosView();
        setData(permisos);
        setFilteredData(permisos);
      } catch (error) {
        console.error("Error fetching permisos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) =>
        item.nombre.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchValue, data]);

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Permisos");

    worksheet.columns = [
      { header: "ID", key: "id", width: 10 },
      { header: "ID_USUARIO", key: "id_usuario", width: 15 },
      { header: "NOMBRE", key: "nombre", width: 30 },
      { header: "Fecha Permiso", key: "permiso_fecha", width: 15 },
    ];

    const exportableData = filteredData.map((row) => ({
      id: row.id,
      id_usuario: row.id_usuario,
      nombre: row.nombre,
      permiso_fecha: row.permiso_fecha
        ? dayjs(row.permiso_fecha).format("DD/MM/YYYY")
        : "",
    }));

    worksheet.addRows(exportableData);

    // Style header row
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF143F04" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Style all data cells borders
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber !== 1) {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Permisos_${dayjs().format("YYYYMMDD")}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header title="Permisos" subtitle="Listado de permisos" />
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            sx={{
              background: "#143f04",
              color: "white",
              borderRadius: "8px",
              height: "3rem",
              "&:hover": { background: "#36a60f" },
            }}
            onClick={handleExport}
            variant="contained"
          >
            Exportar
          </Button>
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height={"50%"}
            width={"100%"}
            ml={1}
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar por nombre"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row.id}
          components={{ Toolbar: CustomToolbar }}
          pageSize={100}
          rowsPerPageOptions={[100]}
          autoHeight={false}
        />
      </Box>
      <ModalCharge isLoading={isLoading} />
    </Box>
  );
};

export default PermisosView;